import React, { memo, useMemo } from "react";
import { Button, HStack, Spacer, Text, VStack } from "@chakra-ui/react";
import {
  ChatsIcon,
  CircleCheckIcon,
  LinkIcon,
  ThreadIcon,
} from "modules/icons";
import colors from "app/colors";
import { NO_SUBJECT } from "modules/utils/Constants";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import CopyCalloutsPopover from "./copy-callouts-popover";
import CopyFilesPopover from "./copy-files-popover";
import CopyTagsPopover from "./copy-tags-popover";
import { MessageThreadType } from "gql/graphql";
import LinkedConversationHelpButton from "modules/_shared/help-buttons/linked-conversation-help-button";
import { IQuillEditorHandle } from "modules/messaging/compose/quill-message-composer";

interface IProps {
  draftId: string;
  quillEditorRef: React.RefObject<IQuillEditorHandle>;
}

const ForwardToRelatedMessageThread = ({ draftId, quillEditorRef }: IProps) => {
  const { draft, toggleRelatedMessageThread } = useMessageThreadDraft({
    draftId,
  });

  const relatedMessageThread = draft?.relatedMessageThread;

  const headerIcon = useMemo(() => {
    switch (relatedMessageThread?.type) {
      case MessageThreadType.Thread:
        return <ThreadIcon boxSize={5} fill={colors.orangePrimary} />;
      case MessageThreadType.Chat:
        return <ChatsIcon boxSize={5} fill={colors.blue} />;
      case MessageThreadType.Task:
        return <CircleCheckIcon boxSize={5} fill={colors.greenPrimary} />;
      default:
        return null;
    }
  }, [relatedMessageThread?.type]);

  if (!draft || !relatedMessageThread) {
    return null;
  }

  return (
    <HStack w="full" alignItems="flex-start">
      <LinkIcon boxSize={6} fill={colors.gray40} />
      <VStack w="full" alignItems="flex-start" spacing={0}>
        <HStack w="full" spacing={1}>
          {headerIcon}
          <Text noOfLines={1} fontSize={14}>
            {relatedMessageThread.subject || NO_SUBJECT}
          </Text>
          <LinkedConversationHelpButton />
          <Spacer />
          <Button
            variant="link"
            color={colors.gray40}
            fontSize={12}
            fontWeight={500}
            onClick={toggleRelatedMessageThread}
          >
            {relatedMessageThread.isRemoved ? "+ Add Link" : "- Unlink"}
          </Button>
        </HStack>
        <HStack w="full">
          <CopyCalloutsPopover
            draftId={draftId}
            quillEditorRef={quillEditorRef}
          />
          <CopyFilesPopover
            draftId={draftId}
            relatedMessageThread={relatedMessageThread}
          />
          <CopyTagsPopover
            draftId={draftId}
            relatedMessageThread={relatedMessageThread}
            quillEditorRef={quillEditorRef}
          />
        </HStack>
      </VStack>
    </HStack>
  );
};

export default memo(ForwardToRelatedMessageThread);
